import { Card, Icon, Tooltip } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Table from "examples/Tables/Table";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import NoData from "containers/DataNotAvailBox";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchGeneralFollowups } from "services/generalFollowup";
import { globaleDateTimeFormatter } from "utils/helper";
import { dashboardTodayAllTabsSx } from "utils/dashboardTodayAllTabsSx";

function TodaysFollowup({ title, tooltip, uid }) {
  const [filterMode, setFilterMode] = useState("today");
  const [loader, setLoader] = useState(false);
  const [followups, setFollowups] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoader(true);
      setFollowups(await fetchGeneralFollowups({ uid, filterMode }));
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  }, [uid, filterMode]);

  useEffect(() => {
    if (uid) load();
  }, [uid, load]);

  const columns = [
    { name: "Client Name", align: "left", minWidth: "120px" },
    { name: "Date/Time", align: "left", minWidth: "140px" },
    { name: "Mobile number", align: "left", minWidth: "120px" },
    { name: "Notes", align: "left", minWidth: "120px" },
  ];

  const rows = useMemo(
    () =>
      followups.map((item) => ({
        "Client Name": (
          <SuiTypography
            variant="button"
            fontWeight="medium"
            style={{ cursor: "pointer", paddingLeft: 14 }}
            onClick={() => item.clientId && navigate(`/clients/${item.clientId}`)}
          >
            {item.clientName ?? "—"}
          </SuiTypography>
        ),
        "Date/Time": (
          <SuiTypography variant="button" fontWeight="medium">
            {globaleDateTimeFormatter(item.followUpDate)}
          </SuiTypography>
        ),
        "Mobile number": (
          <SuiTypography variant="button" fontWeight="medium" paddingLeft={2}>
            {item.phoneNumber ?? "—"}
          </SuiTypography>
        ),
        Notes: (
          <SuiTypography variant="button" fontWeight="medium">
            {item.notes?.length > 40 ? `${item.notes.slice(0, 40)}...` : item.notes || "—"}
          </SuiTypography>
        ),
      })),
    [followups, navigate]
  );

  return (
    <SuiBox mb={3} style={{ height: "100%" }}>
      <Card style={{ overflow: "inherit", height: "100%" }}>
        <SuiBox style={{ overflow: "hidden", borderRadius: 20 }}>
          <SuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            px={2.5}
            pt={2.5}
            gap={1}
          >
            <SuiTypography display="flex" alignItems="center">
              <SuiTypography variant="h6">{title}</SuiTypography>
              <Tooltip title={tooltip} placement="top">
                <Icon sx={{ ml: 1, fontSize: 16, color: "#67748e" }}>help</Icon>
              </Tooltip>
            </SuiTypography>
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={(_, v) => v && setFilterMode(v)}
              size="small"
              sx={dashboardTodayAllTabsSx}
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </SuiBox>
          <SuiBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ maxHeight: 185, minHeight: rows.length === 0 ? 185 : 0 }}
          >
            {!loader ? (
              <SuiBox
                id="tableBox"
                style={{ overflow: "auto", width: "100%", borderRadius: "1rem" }}
                maxHeight={185}
                sx={{
                  "& .MuiTableRow-root:not(:last-child) td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                }}
              >
                {rows.length ? (
                  <Table columns={columns} rows={rows} isOverflow />
                ) : (
                  <SuiBox height={185} display="flex" justifyContent="center" alignItems="center">
                    <NoData
                      message={
                        filterMode === "today"
                          ? "No follow-ups scheduled for today"
                          : "No follow-ups found"
                      }
                    />
                  </SuiBox>
                )}
              </SuiBox>
            ) : (
              <TailSpin color="#17c1e8" height={40} width={40} />
            )}
          </SuiBox>
        </SuiBox>
      </Card>
    </SuiBox>
  );
}

export default TodaysFollowup;
